import {
  type ConsumerWebhook,
  type ConsumerWebhookDelivery,
  type ConsumerWebhookDeliveryList,
  type ConsumerWebhookState,
  type ConsumerWebhookUrlInput,
  type ConsumerWebhookWithSecret,
} from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from './client';
import { apiRequest } from './client';
import { consumerEventsQueryKey, consumersQueryKey } from './consumers';

/**
 * Notifica di cambio stato di un consumer (DEC-013, #84/#85): configurazione, ultime consegne e
 * azioni dell'amministratore su `/api/v1/consumers/:id/webhook`. La chiave della configurazione è
 * il prefisso di quella delle consegne, così ogni azione le invalida entrambe.
 */
export const consumerWebhookQueryKey = (id: string) => ['consumers', id, 'webhook'] as const;
export const consumerWebhookDeliveriesQueryKey = (id: string) => ['consumers', id, 'webhook', 'deliveries'] as const;

/** Ogni quanto si rilegge l'esito finché un evento di prova è in coda (il worker passa ogni 3 s). */
export const TEST_DELIVERY_POLL_MS = 2000;

/**
 * Per quanto, al massimo, si segue l'evento di prova appena inviato: un tentativo dura al più 10 s
 * più il passaggio del worker, oltre si smette e resta «Aggiorna».
 */
export const TEST_DELIVERY_POLL_MAX_MS = 60_000;

/** `GET /consumers/:id/webhook`: `webhook: null` se la notifica non è mai stata attivata. */
export function useConsumerWebhook(id: string) {
  return useQuery({
    queryKey: consumerWebhookQueryKey(id),
    queryFn: () => apiRequest<ConsumerWebhookState>(`/consumers/${id}/webhook`),
  });
}

/**
 * `GET /consumers/:id/webhook/deliveries`: ultime consegne, dalla più recente. Con `followEventId`
 * (l'evento di prova appena inviato, finché chi chiama lo segue) la lista si rilegge da sola mentre
 * quell'evento non c'è ancora o è in coda; si ferma se la lettura fallisce.
 */
export function useConsumerWebhookDeliveries(id: string, followEventId: string | null = null) {
  return useQuery({
    queryKey: consumerWebhookDeliveriesQueryKey(id),
    queryFn: () => apiRequest<ConsumerWebhookDeliveryList>(`/consumers/${id}/webhook/deliveries`),
    refetchInterval: (query) => {
      if (!followEventId || query.state.status === 'error') return false;
      const followed = query.state.data?.deliveries.find((delivery) => delivery.id === followEventId);
      return !followed || followed.status === 'in_coda' ? TEST_DELIVERY_POLL_MS : false;
    },
  });
}

/**
 * Una mutazione sulla notifica: dopo, riuscita o no, configurazione, consegne e storico del consumer
 * si rileggono (un 409 vuol dire che un altro amministratore ha appena cambiato lo stato), e con loro
 * il registro, che mostra la colonna «Notifica» e, dopo `consumers.already_revoked`, la revoca. `secret`: la risposta porta
 * il segreto di firma, quindi `gcTime: 0` perché dopo il `reset()` non resti nella cache delle mutation.
 */
function useWebhookMutation<TResult, TInput = void>(
  id: string,
  request: (input: TInput) => { path: string; method: 'POST' | 'PATCH'; body?: unknown },
  { secret = false }: { secret?: boolean } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    ...(secret ? { gcTime: 0 } : {}),
    mutationFn: (input) => {
      const { path, method, body } = request(input);
      return apiRequest<TResult>(`/consumers/${id}/webhook${path}`, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: consumerWebhookQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: consumerEventsQueryKey(id) }),
        // Il registro mostra la colonna «Notifica» e, dopo un 409 `already_revoked`, la revoca.
        queryClient.invalidateQueries({ queryKey: consumersQueryKey, exact: true }),
      ]),
  });
}

/** `POST /consumers/:id/webhook`: attiva o riattiva; il segreto di firma è **solo** in questa risposta. */
export function useActivateConsumerWebhook(id: string) {
  return useWebhookMutation<ConsumerWebhookWithSecret, ConsumerWebhookUrlInput>(
    id,
    (input) => ({ path: '', method: 'POST', body: input }),
    { secret: true }
  );
}

/** `PATCH /consumers/:id/webhook`: nuovo indirizzo di una notifica attiva, stesso segreto. */
export function useUpdateConsumerWebhookUrl(id: string) {
  return useWebhookMutation<ConsumerWebhook, ConsumerWebhookUrlInput>(id, (input) => ({
    path: '',
    method: 'PATCH',
    body: input,
  }));
}

/** `POST /consumers/:id/webhook/rotate-secret`: segreto nuovo (solo qui), il precedente vale ancora 24 ore. */
export function useRotateConsumerWebhookSecret(id: string) {
  return useWebhookMutation<ConsumerWebhookWithSecret>(id, () => ({ path: '/rotate-secret', method: 'POST' }), {
    secret: true,
  });
}

/** `POST /consumers/:id/webhook/disable`: nessun nuovo evento, quelli in coda scartati, segreti cancellati. */
export function useDisableConsumerWebhook(id: string) {
  return useWebhookMutation<ConsumerWebhook>(id, () => ({ path: '/disable', method: 'POST' }));
}

/** `POST /consumers/:id/webhook/test` (202): accoda un evento di prova con un solo tentativo. */
export function useSendConsumerWebhookTest(id: string) {
  return useWebhookMutation<ConsumerWebhookDelivery>(id, () => ({ path: '/test', method: 'POST' }));
}
